import { useMutation, useQueryClient } from "@tanstack/react-query";
import { neon } from "@/integrations/neon";
import type { UpdateVersionInput } from "../types";

export const useUpdateVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateVersionInput) => {
      const data = await neon.api.versions.update(input.versionId, {
        versionName: input.versionName,
        scriptUrl: input.scriptUrl,
        notes: input.notes,
        notesTh: input.notesTh,
        passwordProtected: input.passwordProtected,
        reviewPassword: input.reviewPassword,
        isActive: input.isActive,
      });

      return data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["script-versions"] });
      await queryClient.cancelQueries({ queryKey: ["scripts"] });
    },
    onSuccess: (data) => {
      // Invalidate queries to refresh data
      if (data?.script_id) {
        queryClient.invalidateQueries({ queryKey: ["script-versions", data.script_id] });
      }
      queryClient.invalidateQueries({ queryKey: ["scripts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};

export const useSetCurrentVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scriptId, versionId }: { scriptId: string; versionId: string }) => {
      await neon.api.scripts.setCurrentVersion(scriptId, versionId);
      return { scriptId, versionId };
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["script-versions", variables.scriptId] });
      await queryClient.cancelQueries({ queryKey: ["scripts"] });

      // Snapshot previous values
      const previousVersions = queryClient.getQueryData(["script-versions", variables.scriptId]);
      const previousScripts = queryClient.getQueryData(["scripts"]);

      return { previousVersions, previousScripts };
    },
    onError: (_err, variables, context) => {
      // Rollback to previous state on error
      if (context?.previousVersions) {
        queryClient.setQueryData(["script-versions", variables.scriptId], context.previousVersions);
      }
      if (context?.previousScripts) {
        queryClient.setQueryData(["scripts"], context.previousScripts);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["script-versions", data.scriptId] });
      queryClient.invalidateQueries({ queryKey: ["scripts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
};
